import moment from "moment";
import { z } from "zod";

const ZSession = z
  .object({
    id: z.string().min(1),
    created: z
      .date()
      .or(z.string())
      .transform((arg) => moment(arg).toDate()),
    expire: z.boolean().default(false).optional(),
  })
  .strict();

const ZOptions = z.object({
  localStorageKey: z.string().min(3).default("pxss"),
  keepSession: z
    .number()
    .int()
    .min(3)
    /** Default keep is 10, since this is how long instances will be kept alive by default. */
    .default(10),
});

type Options = z.infer<typeof ZOptions>;

/** The sessionId is stored in the localStorage to allow for reconnection and
 * to prevent spamming start requests of instances, when smashing F5.
 */
export class Session {
  private _current?: string;
  public get current() {
    return this._current;
  }

  public get noSession(): boolean {
    return new URLSearchParams(window.location.search).has("noSession");
  }

  public get id(): string | null {
    // If the query-parameter "noSession" or "nosession" is set, it will be skipped.
    if (this.noSession) return null;

    const sessionString = window.localStorage.getItem(this.localStorageKey);
    // No session yet set.
    if (sessionString === null) {
      return null;
    }

    const parsed = ZSession.safeParse(JSON.parse(sessionString));
    // Clear the session if it's not valid.
    if (!parsed.success) {
      this.unset();
      return null;
    }

    const session = parsed.data;
    // Clear the session, if it is expired.
    if (moment(session.created).add(this.options.keepSession, "seconds").isBefore(moment())) {
      this.unset();
      return null;
    }

    // Return the sessionId.
    return session.id;
  }

  private options: Options;

  private get localStorageKey() {
    return this.options.localStorageKey;
  }

  constructor(options?: Options) {
    this.options = ZOptions.parse(options || {});
  }

  /** Set's the session with creation date for the given storage key to the localStorage. */
  public set(sessionId: string): void {
    ZSession.shape.id.parse(sessionId);
    this._current = sessionId;

    const session: z.infer<typeof ZSession> = {
      id: sessionId,
      created: moment().toDate(),
      expire: false,
    };

    window.localStorage.setItem(this.localStorageKey, JSON.stringify(session));
  }

  /** Removes a session from the localStorage. */
  public unset(): void {
    window.localStorage.removeItem(this.localStorageKey);
  }
}
